import { useState } from "react";

import { Select } from "@/components/inputs";
import { ROLE_LABEL, type Organisation, type OrgUser } from "@/modules/organisations/fixtures";

/** Add a user — Fabric's `users/create`.
 *
 *  A name, an email, one organisation and one role. That is the whole record.
 *
 *  NO PASSWORD FIELD, deliberately: creating a user sends a reset link rather
 *  than a password, so no password is ever transmitted (SEC-05). The person
 *  sets their own on first use.
 *
 *  A SUPER ADMIN HAS NO ORGANISATION, and a non-super must have one —
 *  `create_user` refuses both mistakes, so the form refuses them here too
 *  rather than letting the server be the first to say no.
 */
export function InviteUserForm({ organisations, onCancel, onDone }: {
  organisations: Organisation[];
  onCancel: () => void;
  onDone: (email: string) => void;
}) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [org, setOrg] = useState("");
  const [role, setRole] = useState<OrgUser["role"]>("company_user");
  const [err, setErr] = useState("");

  const superAdmin = role === "super_admin";

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return setErr("A name is required.");
    if (!email.trim()) return setErr("An email address is required — the invitation goes there.");
    if (superAdmin && org) return setErr("A super admin belongs to no organisation. Clear the organisation, or pick another role.");
    if (!superAdmin && !org) return setErr("Pick an organisation — only a super admin may have none.");
    setErr("");
    onDone(email.trim());
  }

  return (
    <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span className="cap">Full name</span>
          <input className="search" value={name} autoFocus style={{ marginLeft: 0, width: 220 }}
            placeholder="Anita Deshmukh" onChange={(e) => setName(e.target.value)} />
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span className="cap">Email</span>
          <input className="search mono" type="email" value={email} style={{ marginLeft: 0, width: 260 }}
            placeholder="anita@nexcell.in" onChange={(e) => setEmail(e.target.value)} />
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span className="cap">Role</span>
          <Select value={role} onChange={(e) => setRole(e.target.value as OrgUser["role"])} style={{ minWidth: 170 }}>
            {Object.entries(ROLE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </Select>
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span className="cap">Organisation</span>
          <Select value={org} disabled={superAdmin} onChange={(e) => setOrg(e.target.value)} style={{ minWidth: 210 }}>
            <option value="">{superAdmin ? "None — super admins have no company" : "Choose an organisation"}</option>
            {organisations.map((o) => (
              <option key={o.organisation_id} value={o.organisation_id}>{o.name}</option>
            ))}
          </Select>
        </label>
      </div>

      <div className="cap">
        No password is set here. The invitation carries a reset link, so the person chooses their own
        and nothing is ever sent in the clear.
      </div>

      {err && <div style={{ fontSize: 12.5, color: "var(--bad)" }}>{err}</div>}

      <div style={{ display: "flex", gap: 8 }}>
        <button type="submit" className="btn teal sm">Send invitation</button>
        <button type="button" className="btn ghost sm" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}
